import React, { useEffect, useRef, useState } from 'react';
import '../assets/css/systemManagement.css';
import { UsersModel } from '../models/UsersModel';
import {
    FilterDataModel,
    SortModel,
    SortDirection,
    ActivityStatus,
    UserModel,
    RoleModel,
    BranchModel,
    OfficeDepartmentModel,
} from '../models/superbit-api-models';
import Pagger from '../components/_Pagger';
import DeleteAlert from '../components/DeleteAlert';
import NewUser from './_NewUser';
import TreeSelectInput, { TreeNode } from '../components/_TreeSelectInput';

// Flatten the API branch/office-department lists into the flat id/name/parentId
// nodes TreeSelectInput builds its forest from (same mapping _NewUser uses).
const toTreeNodes = (items?: (BranchModel | OfficeDepartmentModel)[]): TreeNode[] =>
    (items || []).map((i) => ({ id: i.id, name: i.name, parentId: i.parentId }));

const PAGE_SIZE = 50; // server-side page size (Constants.NUMBER_OF_OBJECTS_PER_PAGE)

// Sortable columns — `sortBy` matches the property names the old MVC view sent
// (data-sort-by) so the existing server-side sorting keeps working.
type Column = { key: string; label: string; sortBy?: string };
const COLUMNS: Column[] = [
    { key: 'fullName', label: 'שם מלא', sortBy: 'FullName' },
    { key: 'userName', label: 'שם משתמש', sortBy: 'UserName' },
    { key: 'role', label: 'קבוצה', sortBy: 'RoleId' },
    { key: 'department', label: 'מחלקה', sortBy: 'Department' },
    { key: 'salesPerson', label: 'איש מכירות' },
    { key: 'status', label: 'סטטוס', sortBy: 'Status' },
];

type Props = {
    // Optional hooks for the (separately ported) add/edit user modal.
    onAddUser?: () => void;
    onEditUser?: (user: UserModel) => void;
};

const UsersManagement = (props: Props) => {
    const [usersModel] = useState<UsersModel>(() => new UsersModel());

    const [users, setUsers] = useState<UserModel[]>([]);
    const [roles, setRoles] = useState<RoleModel[]>([]);
    const [salesPeople, setSalesPeople] = useState<Record<string, string>>({});
    const [branches, setBranches] = useState<TreeNode[]>([]);
    const [loading, setLoading] = useState<boolean>(false);

    const [searchTerm, setSearchTerm] = useState<string>('');
    const [roleId, setRoleId] = useState<string>('');
    const [branchId, setBranchId] = useState<string>('');
    const [branchName, setBranchName] = useState<string>('');
    const [status, setStatus] = useState<string>(''); // '' = all, '0' = active, '1' = inactive

    const [sortProp, setSortProp] = useState<string | undefined>(undefined);
    const [sortDir, setSortDir] = useState<SortDirection>(SortDirection._0);

    const [page, setPage] = useState<number>(1);
    const [totalItems, setTotalItems] = useState<number>(0);
    const [reloadTick, setReloadTick] = useState<number>(0); // bumped to force a reload (e.g. on refresh/clear)

    const [openMenuId, setOpenMenuId] = useState<string | undefined>(undefined);
    const [deleteId, setDeleteId] = useState<string | undefined>(undefined);

    // Add/edit user modal (ported _NewUser). undefined editUserId = add mode.
    const [showUserModal, setShowUserModal] = useState<boolean>(false);
    const [editUserId, setEditUserId] = useState<string | undefined>(undefined);

    const buildFilter = (): FilterDataModel => {
        const filter = new FilterDataModel();
        filter.searchTerm = searchTerm ? searchTerm : undefined;
        filter.roleId = roleId ? roleId : undefined;
        filter.branchId = branchId ? branchId : undefined;
        // Leave status undefined to mean "all" — the server only filters when set.
        filter.status = status !== '' ? (parseInt(status, 10) as ActivityStatus) : undefined;
        filter.page = page;
        if (sortProp) {
            const sort = new SortModel();
            sort.property = sortProp;
            sort.sortDirection = sortDir;
            filter.sortBy = [sort];
        }
        return filter;
    };

    const load = async () => {
        setLoading(true);
        try {
            const result = await usersModel.userSort(buildFilter());
            setUsers(result && result.users ? result.users : []);
            setTotalItems(result && result.allRowsCounter ? result.allRowsCounter : 0);
        } catch (e) {
            console.error('Failed to load users', e);
            setUsers([]);
            setTotalItems(0);
        } finally {
            setLoading(false);
        }
    };

    const loadRoles = async () => {
        try {
            const result = await usersModel.getRoles();
            setRoles(result ? result : []);
        } catch (e) {
            console.error('Failed to load roles', e);
        }
    };

    // One add-mode form load gives us the sales-people list (for the column) and
    // the branch / office-department trees (for the filters).
    const loadLists = async () => {
        try {
            const model = await usersModel.getFormLists();
            const map: Record<string, string> = {};
            (model && model.salesPeople ? model.salesPeople : []).forEach((sp) => {
                if (sp.value) map[sp.value] = sp.text || '';
            });
            setSalesPeople(map);
            setBranches(toTreeNodes(model && model.branches ? model.branches : []));
        } catch (e) {
            console.error('Failed to load filter lists', e);
        }
    };

    useEffect(() => {
        loadRoles();
        loadLists();
    }, []);

    // Reload whenever the filters, sort or page change (reloadTick forces a reload).
    useEffect(() => {
        load();
    }, [roleId, branchId, status, sortProp, sortDir, page, reloadTick]);

    const onSearchKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
        if (e.key === 'Enter') {
            applySearch();
        }
    };

    const applySearch = () => {
        if (page !== 1) {
            setPage(1); // page change triggers reload
        } else {
            load();
        }
    };

    // Refresh button: clear all filters and reload (reloadTick guarantees the reload).
    const clearFilters = () => {
        setSearchTerm('');
        setRoleId('');
        setBranchId('');
        setBranchName('');
        setStatus('');
        setPage(1);
        setReloadTick((t) => t + 1);
    };

    // Auto-search: reload shortly after the user stops typing (skip the initial mount).
    const searchDidMount = useRef(false);
    useEffect(() => {
        if (!searchDidMount.current) {
            searchDidMount.current = true;
            return;
        }
        const t = setTimeout(() => applySearch(), 400);
        return () => clearTimeout(t);
    }, [searchTerm]);

    const onSort = (column: Column) => {
        if (!column.sortBy) return;
        if (sortProp === column.sortBy) {
            setSortDir(sortDir === SortDirection._0 ? SortDirection._1 : SortDirection._0);
        } else {
            setSortProp(column.sortBy);
            setSortDir(SortDirection._0);
        }
    };

    const onChangePage = (newPage: number) => {
        setPage(newPage);
    };

    const toggleMenu = (id: string | undefined) => {
        if (!id) return;
        setOpenMenuId(openMenuId === id ? undefined : id);
    };

    const onEdit = (user: UserModel) => {
        setOpenMenuId(undefined);
        // Optional parent override; otherwise open the built-in modal.
        if (props.onEditUser) {
            props.onEditUser(user);
            return;
        }
        setEditUserId(user.id);
        setShowUserModal(true);
    };

    const onAdd = () => {
        if (props.onAddUser) {
            props.onAddUser();
            return;
        }
        setEditUserId(undefined);
        setShowUserModal(true);
    };

    const confirmDelete = async () => {
        if (!deleteId) return;
        try {
            await usersModel.userDelete(deleteId);
            await load();
        } catch (e) {
            console.error('Failed to delete user', e);
        } finally {
            setDeleteId(undefined);
        }
    };

    const departmentText = (user: UserModel) => {
        return [user.branchName, user.officeDepartmentName].filter((x) => !!x).join(' ');
    };

    const isActive = (s?: ActivityStatus) => s === ActivityStatus._0; // 0 = Active (פעיל)

    const pagerConf = {
        pageSize: PAGE_SIZE,
        currentPage: page,
        totalItems: totalItems,
    };

    return (
        <div className="users-management rtl">
            <div className="users-toolbar flex-centered">
                <div className="users-filter">
                    <select
                        className="users-filter__item"
                        value={status}
                        onChange={(e) => {
                            setStatus(e.target.value);
                            setPage(1);
                        }}
                    >
                        <option value="">סטטוס</option>
                        <option value="0">פעיל</option>
                        <option value="1">לא פעיל</option>
                    </select>
                </div>
                <div className="users-filter">
                    <select
                        className="users-filter__item"
                        value={roleId}
                        onChange={(e) => {
                            setRoleId(e.target.value);
                            setPage(1);
                        }}
                    >
                        <option value="">קבוצה</option>
                        {roles.map((role) => (
                            <option key={role.id} value={role.id}>
                                {role.name}
                            </option>
                        ))}
                    </select>
                </div>
                <div className="users-filter users-filter--tree">
                    <TreeSelectInput
                        title=""
                        placeholder="סניף"
                        items={branches}
                        value={branchId}
                        valueName={branchName}
                        onChange={(id, name) => {
                            setBranchId(id || '');
                            setBranchName(name || '');
                            setPage(1);
                        }}
                    />
                </div>
                <div className="users-search">
                    <span className="search-icon" />
                    <input
                        type="text"
                        className="users-search__input"
                        placeholder="חיפוש"
                        autoComplete="off"
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        onKeyDown={onSearchKeyDown}
                    />
                </div>
                <div className="users-refresh pointer" title="רענון" onClick={() => clearFilters()} />
            </div>

            <div className="users-table-wrapper">
                <table className="users-table">
                    <thead>
                        <tr>
                            {COLUMNS.map((col) => (
                                <th
                                    key={col.key}
                                    className={`users-th ${col.sortBy ? 'sortable pointer' : ''} ${
                                        sortProp === col.sortBy ? 'sorted' : ''
                                    }`}
                                    onClick={() => onSort(col)}
                                >
                                    <span>{col.label}</span>
                                    {col.sortBy && (
                                        <span
                                            className={`arrow-updown ${
                                                sortProp === col.sortBy
                                                    ? sortDir === SortDirection._0
                                                        ? 'asc'
                                                        : 'desc'
                                                    : ''
                                            }`}
                                        />
                                    )}
                                </th>
                            ))}
                            <th className="users-th users-th--add">
                                <div className="add-btn pointer" onClick={onAdd}>
                                    <span className="add-file" />
                                    <span className="text">הוסף</span>
                                </div>
                            </th>
                        </tr>
                    </thead>
                    <tbody>
                        {users.map((user) => (
                            <tr key={user.id}>
                                <td className="bold">{user.fullName}</td>
                                <td>{user.userName}</td>
                                <td>{user.roleName}</td>
                                <td>{departmentText(user)}</td>
                                <td>{user.salesPersonId ? salesPeople[user.salesPersonId] || '' : ''}</td>
                                <td className={isActive(user.status) ? 'online' : 'offline'}>
                                    <span>{isActive(user.status) ? 'פעיל' : 'לא פעיל'}</span>
                                    <span className="dot" />
                                </td>
                                <td className="users-actions">
                                    <button
                                        type="button"
                                        className="actionBtn pointer"
                                        onClick={() => toggleMenu(user.id)}
                                    >
                                        <div className="edit-icon" />
                                        <span>פעולות</span>
                                        <div className="dropdown-icon" />
                                    </button>
                                    {openMenuId === user.id && (
                                        <ul className="actionBtnMenu">
                                            <li className="actionBtnMenuItem" onClick={() => onEdit(user)}>
                                                ערוך
                                            </li>
                                            <li
                                                className="actionBtnMenuItem"
                                                onClick={() => {
                                                    setOpenMenuId(undefined);
                                                    setDeleteId(user.id);
                                                }}
                                            >
                                                מחק
                                            </li>
                                        </ul>
                                    )}
                                </td>
                            </tr>
                        ))}
                        {!loading && users.length === 0 && (
                            <tr>
                                <td className="users-empty" colSpan={COLUMNS.length + 1}>
                                    לא נמצאו משתמשים
                                </td>
                            </tr>
                        )}
                    </tbody>
                </table>
            </div>

            {totalItems > 0 && (
                <div className="users-pager">
                    <Pagger pagerConf={pagerConf} changePage={onChangePage} refresh={() => load()} />
                </div>
            )}

            <DeleteAlert
                show={!!deleteId}
                onClose={() => setDeleteId(undefined)}
                onConfirm={confirmDelete}
            />

            <NewUser
                show={showUserModal}
                userId={editUserId}
                onClose={() => setShowUserModal(false)}
                onSaved={() => {
                    setShowUserModal(false);
                    load();
                }}
            />
        </div>
    );
};

export default UsersManagement;
export {};
